import React, { useRef, useState } from 'react';
import {
  View,
  TextInput,
  StyleSheet,
  Text,
  Platform,
} from 'react-native';
import { fpColors } from '../styles/theme';

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value,
  onChange,
  error,
  disabled = false,
  autoFocus = true,
  placeholder = '•',
}) => {
  const inputRefs = useRef<(TextInput | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(autoFocus ? 0 : null);

  // Ensure value is always the correct length
  const normalizedValue = value.padEnd(length, '').slice(0, length);

  const handleChangeText = (text: string, index: number) => {
    // Handle paste operation - if pasting multiple characters
    if (text.length > 1) {
      // Extract only digits from pasted text
      const pastedDigits = text.replace(/\D/g, '');
      
      if (pastedDigits.length > 0) {
        // Create new value by taking digits up to our length limit
        const newDigits = pastedDigits.slice(0, length);
        
        // Pad with spaces to maintain full length
        const paddedValue = newDigits.padEnd(length, '');
        
        // Update the parent component with the new value
        onChange(newDigits);
        
        // Focus the next empty input or the last filled input
        const focusIndex = Math.min(newDigits.length, length - 1);
        setTimeout(() => {
          inputRefs.current[focusIndex]?.focus();
          setFocusedIndex(focusIndex);
        }, 10);
      }
      return;
    }

    // Handle single character input or deletion
    const digit = text.replace(/\D/g, ''); // Only allow digits
    
    // Get current value as array
    const currentArray = normalizedValue.split('');
    
    // Update the specific index
    currentArray[index] = digit;
    
    // Join and clean up the value
    const newValue = currentArray.join('').replace(/\s/g, '');
    onChange(newValue);

    // Auto-focus next input if we added a digit
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  const handleKeyPress = (key: string, index: number) => {
    if (key === 'Backspace') {
      const valueArray = normalizedValue.split('');
      
      if (valueArray[index]) {
        // Clear current input
        valueArray[index] = '';
        onChange(valueArray.join('').replace(/\s/g, ''));
      } else if (index > 0) {
        // Move to previous input and clear it
        valueArray[index - 1] = '';
        onChange(valueArray.join('').replace(/\s/g, ''));
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      }
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  return (
    <View style={styles.container}>
      <View style={styles.inputContainer}>
        {Array.from({ length }, (_, index) => (
          <TextInput
            key={index}
            ref={(ref) => (inputRefs.current[index] = ref)}
            style={[
              styles.input,
              focusedIndex === index ? styles.inputFocused : null,
              error ? styles.inputError : null,
              disabled ? styles.inputDisabled : null,
            ]}
            value={normalizedValue[index] || ''}
            onChangeText={(text) => handleChangeText(text, index)}
            onKeyPress={({ nativeEvent }) => handleKeyPress(nativeEvent.key, index)}
            onFocus={() => handleFocus(index)}
            onBlur={handleBlur}
            placeholder={placeholder}
            placeholderTextColor={fpColors.outlineVariant}
            keyboardType="numeric"
            maxLength={1}
            selectTextOnFocus
            editable={!disabled}
            autoFocus={autoFocus && index === 0}
            textAlign="center"
            contextMenuHidden={Platform.OS === 'ios'} // Disable context menu on iOS
          />
        ))}
      </View>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  inputContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 6,
  },
  input: {
    // Fluid width — 6 inputs share the row, never overflow narrow viewports.
    flex: 1,
    minWidth: 0,
    maxWidth: 52,
    aspectRatio: 0.8,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLow,
    fontFamily: 'Inter_600SemiBold',
    fontSize: 20,
    color: fpColors.onSurface,
    textAlign: 'center',
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  inputFocused: {
    borderColor: fpColors.primary,
    borderWidth: 2,
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 2,
  },
  inputError: {
    borderColor: fpColors.error,
    backgroundColor: fpColors.errorContainer,
  },
  inputDisabled: {
    backgroundColor: fpColors.surfaceContainer,
    color: fpColors.outline,
  },
  errorText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.error,
    textAlign: 'center',
    marginTop: 8,
  },
}); 